// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md section 4.7, DEC-320, DEC-321; design/mockups/
// sec2-s3-6 to sec2-s3-11): Settings > Devices (UX84, DEC-336: the file keeps its tablets name). A manager (admin.device.manage) sees every
// device with its name, location and last use, adds a device by making a setup code, cancels a code nobody has used, and
// revokes a device.
//
// Admin surface, touch only: every target is 44px or taller and nothing waits on hover. The location is chosen from a
// list the server sent, never typed, and nothing is pre-chosen (no default location, no default name). Every sentence
// comes from ui/src/tabletsModel.ts; every piece of text is set with textContent, because a device's name is typed
// by a person and must never be read as markup. innerHTML is used only for the small fixed icons below.
//
// The setup code is shown once, on the sheet that made it, and its text is erased from the page when the sheet
// closes. It is never logged and never stored. The sheet counts down the code's 15 minutes and turns orange under
// two minutes; the same one second timer keeps the waiting list's times current without rebuilding any button.

import { sortByName } from "./nameSort.ts";
import { collapseRowActions } from "./rowMenu.ts";
import {
  issueTabletSetupCode,
  listTabletDevices,
  listTabletSetupCodes,
  renameTablet,
  revokeTablet,
  revokeTabletSetupCode,
  type ApiErrorBody,
  type ApiResult,
} from "./api.ts";
import { describeSetting } from "./settingsDescription.ts";
import { checkDeviceName } from "./terminalSetupModel.ts";
import {
  MAX_NAME_LENGTH,
  OTHER_NAME,
  TABLET_NAME_PRESETS,
  buildTabletRows,
  buildWaitingCodes,
  canMakeCode,
  chosenLocationId,
  chosenName,
  leavesNamingToDevice,
  codeCountdown,
  initialAddForm,
  issueFailureView,
  MAX_WAITING_CODES,
  type AddFormState,
  type DeviceDto,
  type LocationDto,
  type SetupCodeDto,
  type TabletRow,
  type WaitingCodeRow,
} from "./tabletsModel.ts";

export interface TabletsSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.device.manage. */
  canManage: () => boolean;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  newKey: () => string;
  lockScroll: () => void;
  unlockScroll: () => void;
}

export interface TabletsSection {
  /** Fetches devices and waiting codes, and draws the section. */
  refresh: () => Promise<void>;
  /** Forgets everything, including any code on screen; the next employee starts clean. */
  reset: () => void;
  /** Closes any open sheet and erases a code from the page (sign-out teardown). */
  dismissSheets: () => void;
}

const SVG = {
  tablet:
    '<svg class="setup-ico" viewBox="0 0 24 20" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="19" cy="10" r="0.9" fill="currentColor"/></svg>',
  info: '<svg class="setup-ico" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M10 9v5M10 5.8v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
} as const;

interface ElOpts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.cls !== undefined) node.className = opts.cls;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.testid !== undefined) node.dataset["testid"] = opts.testid;
  for (const [name, value] of Object.entries(opts.attrs ?? {})) {
    node.setAttribute(name, value);
  }
  for (const child of children) node.append(child);
  return node;
}

/** A label and the control under it, kept close together (the sheet's own gap separates fields, not label from control). */
function field(...children: Node[]): HTMLElement {
  const node = el("div", { cls: "tablets-field" });
  node.append(...children);
  return node;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Sets innerHTML, so it must only ever receive the constant SVG strings in SVG above, never text from the server
 * or a person. */
function icon(markup: string): HTMLElement {
  const holder = el("span", { attrs: { "aria-hidden": "true" } });
  holder.innerHTML = markup;
  return holder;
}

function button(label: string, cls: string, testid: string, onTap: () => void): HTMLButtonElement {
  const node = el("button", { cls, text: label, testid, attrs: { type: "button" } });
  node.addEventListener("click", onTap);
  return node;
}

type Sheet =
  | { kind: "add" }
  | { kind: "limit" }
  | { kind: "revoke"; row: TabletRow; error: string | undefined; busy: boolean }
  | { kind: "rename"; row: TabletRow; typed: string; error: string | undefined; busy: boolean }
  | {
      kind: "code";
      code: string;
      setupCode: SetupCodeDto;
      businessName: string;
      locationName: string;
      error: string | undefined;
    };

export function mountTabletsSection(root: HTMLElement, deps: TabletsSectionDeps): TabletsSection {
  let devices: DeviceDto[] = [];
  let codes: SetupCodeDto[] = [];
  let locations: LocationDto[] = [];
  let businessName = "";
  let loadState: "idle" | "loading" | "failed" | "denied" = "idle";
  let showRevoked = false;
  let sheet: Sheet | undefined;
  let form: AddFormState = initialAddForm();
  let formError: string | undefined;
  let making = false;
  let notice: string | undefined;
  // One key per (location, name) a manager is making, kept across a retry so a double tap or a lost answer can never
  // make two codes.
  let issueAttempt: { signature: string; key: string } | undefined;
  let loadSequence = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  let sheetHost: HTMLElement | undefined;
  let focusListening = false;
  let pollTicks = 0;

  const now = (): number => Date.now();

  // ---------------------------------------------------------------------
  // Loading

  async function refresh(quiet = false): Promise<void> {
    if (!deps.canManage()) {
      loadState = "denied";
      render();
      return;
    }
    const sequence = ++loadSequence;
    if (!quiet) {
      loadState = "loading";
      render();
    }
    const [deviceResult, codeResult] = await Promise.all([
      listTabletDevices(deps.apiBaseUrl(), deps.tenantId()),
      listTabletSetupCodes(deps.apiBaseUrl(), deps.tenantId()),
    ]);
    if (!deps.isCurrent() || sequence !== loadSequence) return;
    if (quiet) {
      // A background re-read never blanks the page or swaps buttons under a finger: it redraws only on a change.
      if (
        deviceResult.status !== 200 ||
        codeResult.status !== 200 ||
        !Array.isArray(deviceResult.body.devices) ||
        !Array.isArray(codeResult.body.setupCodes)
      ) {
        return;
      }
      const changed =
        JSON.stringify(deviceResult.body.devices) !== JSON.stringify(devices) ||
        JSON.stringify(codeResult.body.setupCodes) !== JSON.stringify(codes);
      devices = deviceResult.body.devices;
      locations = deviceResult.body.locations;
      businessName = deviceResult.body.businessName;
      codes = codeResult.body.setupCodes;
      if (changed) render();
      return;
    }
    if (deviceResult.status === 403 || codeResult.status === 403) {
      loadState = "denied";
    } else if (
      deviceResult.status === 200 &&
      codeResult.status === 200 &&
      Array.isArray(deviceResult.body.devices) &&
      Array.isArray(codeResult.body.setupCodes)
    ) {
      devices = deviceResult.body.devices;
      locations = deviceResult.body.locations;
      businessName = deviceResult.body.businessName;
      codes = codeResult.body.setupCodes;
      loadState = "idle";
    } else {
      loadState = "failed";
    }
    render();
  }

  function stopTimer(): void {
    if (focusListening) {
      focusListening = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    }
    pollTicks = 0;
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  }

  function closeSheet(): void {
    if (sheet !== undefined) {
      deps.unlockScroll();
    }
    sheet = undefined;
    sheetHost?.remove();
    sheetHost = undefined;
    formError = undefined;
    making = false;
  }

  function reset(): void {
    loadSequence += 1;
    stopTimer();
    closeSheet();
    devices = [];
    codes = [];
    locations = [];
    businessName = "";
    loadState = "idle";
    showRevoked = false;
    form = initialAddForm();
    notice = undefined;
    issueAttempt = undefined;
    root.replaceChildren();
  }

  function dismissSheets(): void {
    closeSheet();
  }

  // ---------------------------------------------------------------------
  // The page

  function render(): void {
    root.replaceChildren();
    // replaceChildren removed an open sheet's host along with everything else; it is drawn again below.
    sheetHost = undefined;
    // A re-render keeps an open sheet (it lives outside the page content, appended below).
    if (loadState === "denied") {
      root.append(
        el("p", { cls: "tablets-empty", text: "You do not have permission to manage devices." }),
      );
      return;
    }
    const nowMs = now();
    const rows = buildTabletRows(devices, locations, nowMs);
    const waiting = buildWaitingCodes(codes, locations, nowMs);

    if (loadState === "failed") {
      const failed = el("div", { cls: "tablets-card" });
      failed.append(
        el("p", {
          cls: "tablets-empty",
          text: "Could not load the devices. Check your connection and try again.",
        }),
        button("Try again", "tablets-secondary", "tablets-retry", () => void refresh()),
      );
      root.append(failed);
      return;
    }

    if (notice !== undefined) {
      root.append(
        el(
          "div",
          { cls: "setup-msg setup-msg-info tablets-status", testid: "tablets-notice" },
          icon(SVG.info),
          el("span", { text: notice }),
        ),
      );
    }

    // Card 1: the devices
    const card = el("section", { cls: "tablets-card", testid: "tablets-card" });
    const head = el("div", { cls: "tablets-card-head" });
    const headText = el("div");
    headText.append(el("h2", { cls: "tablets-title", text: "Devices" }));
    const addButton = button("Add a device", "tablets-primary", "tablets-add-button", () =>
      openAdd(),
    );
    addButton.disabled = loadState === "loading" && devices.length === 0 && locations.length === 0;
    head.append(headText, addButton);
    card.append(head);

    if (loadState === "loading" && devices.length === 0) {
      card.append(el("p", { cls: "tablets-empty", text: "Loading devices" }));
    } else if (rows.active.length === 0) {
      card.append(
        el("p", {
          cls: "tablets-empty",
          testid: "tablets-empty",
          text: "No devices yet. Add one with a setup code.",
        }),
      );
    }
    // HIGFIX4A (F-28, design/style-guide.md section 9): a chip that repeats on every row says nothing. When two or more
    // devices are all Working the chip is left off and the last used time carries the row.
    const everyWorking = rows.active.length > 1 && rows.active.every((row) => row.tag === "Working");
    for (const row of rows.active) card.append(tabletRow(row, false, everyWorking));
    if (rows.revoked.length > 0) {
      if (showRevoked) {
        for (const row of rows.revoked) card.append(tabletRow(row, true));
      }
      const toggle = button(
        showRevoked
          ? "Hide revoked devices"
          : `Show ${rows.revoked.length} revoked device${rows.revoked.length === 1 ? "" : "s"}`,
        "tablets-secondary tablets-more",
        "tablets-show-revoked",
        () => {
          showRevoked = !showRevoked;
          render();
        },
      );
      toggle.setAttribute("aria-expanded", String(showRevoked));
      card.append(toggle);
    }
    root.append(card);

    // Card 2: codes waiting
    const codesCard = el("section", { cls: "tablets-card", testid: "tablets-codes-card" });
    const codesHead = el("div", { cls: "tablets-card-head" });
    const codesHeadText = el("div");
    codesHeadText.append(el("h2", { cls: "tablets-title", text: "Codes waiting to be used" }));
    const meter = el("span", {
      cls: `tablets-meter${waiting.atLimit ? " is-full" : ""}`,
      testid: "tablets-meter",
      attrs: { "aria-label": `${waiting.count} of ${MAX_WAITING_CODES} codes in use` },
    });
    meter.dataset["count"] = String(waiting.count);
    for (const filled of waiting.pips) {
      meter.append(el("i", { cls: `tablets-pip${filled ? " is-on" : ""}` }));
    }
    meter.append(
      el("span", {
        cls: "tablets-meter-count",
        testid: "tablets-meter-count",
        text: `${waiting.count} of ${MAX_WAITING_CODES}`,
      }),
    );
    codesHead.append(codesHeadText, meter);
    codesCard.append(codesHead);
    if (waiting.rows.length === 0) {
      codesCard.append(el("p", { cls: "tablets-empty", text: "No setup codes waiting." }));
    }
    for (const row of waiting.rows) codesCard.append(codeRow(row));
    root.append(codesCard);

    if (sheet !== undefined) drawSheet();
    ensureTimer(waiting.count > 0 || sheet?.kind === "code");
  }

  function tabletRow(row: TabletRow, revoked: boolean, hideTag = false): HTMLElement {
    const node = el("div", {
      cls: `tablets-row${revoked ? " is-revoked" : ""}`,
      testid: "tablets-row",
    });
    node.dataset["deviceId"] = row.id;
    const nameLine = el("div", { cls: "tablets-row-name" });
    nameLine.append(
      el("span", { text: row.name, testid: "tablets-row-name" }),
      ...(hideTag
        ? []
        : [
            el("span", {
              cls: `tablets-tag${row.tone === "good" ? " is-good" : ""}`,
              text: row.tag,
              testid: "tablets-row-tag",
            }),
          ]),
      // UIFIX5: the last-used time rides the title line, so a row is two lines, not three.
      el("span", { cls: "tablets-row-last-used", text: row.lastUsed, testid: "tablets-row-last-used" }),
    );
    const text = el("div", { cls: "tablets-row-text" });
    text.append(
      nameLine,
      el("div", { cls: "tablets-row-line", text: row.locationName, testid: "tablets-row-location" }),
    );
    node.append(el("span", { cls: "tablets-row-icon" }, icon(SVG.tablet)), text);
    if (!revoked) {
      const actions = el("div", { cls: "tablets-row-actions" });
      actions.append(
        button("Rename", "tablets-secondary", "tablets-rename-button", () => openRename(row)),
        button("Revoke", "tablets-danger-quiet", "tablets-revoke-button", () => openRevoke(row)),
      );
      // HIGFIX4A (F-28): Rename and Revoke sit in the row's one Actions menu; Revoke is never a red button on every row.
      collapseRowActions(actions, { subject: row.name, testId: "tablets-row-menu" });
      node.append(actions);
    }
    return node;
  }

  function codeRow(row: WaitingCodeRow): HTMLElement {
    const node = el("div", { cls: "tablets-row", testid: "tablets-code-row" });
    node.dataset["codeId"] = row.id;
    const nameLine = el("div", { cls: "tablets-row-name" });
    nameLine.append(
      el("span", { text: row.name }),
      el("span", { cls: "tablets-tag is-warn", text: "Waiting for setup" }),
    );
    const count = el("span", {
      cls: `tablets-count${row.low ? " is-low" : ""}`,
      text: row.timeLeft,
      testid: "tablets-code-row-time",
    });
    count.dataset["codeId"] = row.id;
    const text = el("div", { cls: "tablets-row-text" });
    text.append(
      nameLine,
      el("div", { cls: "tablets-row-line", text: row.locationName }),
      el("div", { cls: "tablets-row-line" }, "Code ends in ", count),
    );
    const actions = el("div", { cls: "tablets-row-actions" });
    const cancel = button(
      "Cancel code",
      "tablets-secondary",
      "tablets-cancel-code",
      () => void cancelWaitingCode(row.id),
    );
    actions.append(cancel);
    node.append(el("span", { cls: "tablets-row-icon" }, icon(SVG.tablet)), text, actions);
    return node;
  }

  // ---------------------------------------------------------------------
  // The one second timer: updates times in place, never rebuilds a button

  function ensureTimer(needed: boolean): void {
    if (!needed) {
      stopTimer();
      return;
    }
    if (timer !== undefined) return;
    timer = setInterval(tick, 1000);
    if (!focusListening) {
      focusListening = true;
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", onVisible);
    }
  }

  function onVisible(): void {
    if (document.visibilityState === "hidden" || timer === undefined || !deps.isCurrent()) return;
    void refresh(true);
  }

  function tick(): void {
    if (!deps.isCurrent()) {
      stopTimer();
      return;
    }
    const nowMs = now();
    let anyExpired = false;
    for (const code of codes) {
      if (code.state !== "waiting") continue;
      const countdown = codeCountdown(code, nowMs);
      if (countdown.expired) anyExpired = true;
      for (const node of root.querySelectorAll<HTMLElement>(
        `[data-testid="tablets-code-row-time"][data-code-id="${code.id}"]`,
      )) {
        node.textContent = countdown.text;
        node.classList.toggle("is-low", countdown.low);
      }
    }
    if (sheet?.kind === "code") {
      const countdown = codeCountdown(sheet.setupCode, nowMs);
      const countEl = sheetHost?.querySelector<HTMLElement>(
        '[data-testid="tablets-code-countdown"]',
      );
      const fill = sheetHost?.querySelector<HTMLElement>('[data-testid="tablets-code-bar-fill"]');
      const bar = sheetHost?.querySelector<HTMLElement>('[data-testid="tablets-code-bar"]');
      if (countEl !== null && countEl !== undefined) {
        countEl.textContent = countdown.text;
        countEl.classList.toggle("is-low", countdown.low);
        countEl.dataset["low"] = String(countdown.low);
      }
      if (fill !== null && fill !== undefined)
        fill.style.width = `${Math.round(countdown.fraction * 100)}%`;
      if (bar !== null && bar !== undefined) bar.classList.toggle("is-low", countdown.low);
      if (countdown.expired && sheet.error === undefined) {
        sheet = { ...sheet, error: "This code has ended. Close this and make a new one." };
        drawSheet();
      }
    }
    if (anyExpired) {
      // A code ran out: the list and the meter are re-read from the server rather than guessed.
      void refresh();
    } else if (codes.some((code) => code.state === "waiting")) {
      // A waiting code may have been used on the device: re-read codes and devices every few seconds.
      pollTicks += 1;
      if (pollTicks >= 4) {
        pollTicks = 0;
        void refresh(true);
      }
    }
  }

  // ---------------------------------------------------------------------
  // Sheets

  function mountSheetHost(): HTMLElement {
    if (sheetHost !== undefined) return sheetHost;
    sheetHost = el("div", { cls: "tablets-scrim", testid: "tablets-scrim" });
    root.append(sheetHost);
    return sheetHost;
  }

  function openSheet(next: Sheet): void {
    if (sheet === undefined) deps.lockScroll();
    sheet = next;
    drawSheet();
    ensureTimer(true);
  }

  function drawSheet(): void {
    if (sheet === undefined) return;
    const host = mountSheetHost();
    host.replaceChildren();
    const current = sheet;
    let content: HTMLElement;
    if (current.kind === "add") content = addSheet();
    else if (current.kind === "limit") content = limitSheet();
    else if (current.kind === "revoke") content = revokeSheet(current);
    else if (current.kind === "rename") content = renameSheet(current);
    else content = codeSheet(current);
    host.append(content);
    content.querySelector<HTMLElement>("button")?.focus({ preventScroll: true });
  }

  function sheetShell(title: string, testid: string): { shell: HTMLElement; titleId: string } {
    const titleId = `${testid}-title`;
    const shell = el("div", {
      cls: "tablets-sheet",
      testid,
      attrs: { role: "dialog", "aria-modal": "true", "aria-labelledby": titleId },
    });
    shell.append(el("h2", { text: title, attrs: { id: titleId } }));
    return { shell, titleId };
  }

  function message(
    kind: "warn" | "danger" | "info",
    headline: string | undefined,
    detail: string,
  ): HTMLElement {
    const text = el("span");
    if (headline !== undefined) text.append(el("b", { text: headline }));
    text.append(detail);
    return el(
      "div",
      {
        cls: `setup-msg${kind === "warn" ? " setup-msg-warn" : kind === "info" ? " setup-msg-info" : ""}`,
        testid: "tablets-sheet-message",
        attrs: { role: "alert" },
      },
      icon(SVG.info),
      text,
    );
  }

  // --- Add a device

  function openAdd(): void {
    const waiting = buildWaitingCodes(codes, locations, now());
    if (waiting.atLimit) {
      openSheet({ kind: "limit" });
      return;
    }
    form = initialAddForm();
    formError = undefined;
    making = false;
    openSheet({ kind: "add" });
  }

  function addSheet(): HTMLElement {
    const { shell } = sheetShell("Add a device", "tablets-add-sheet");
    // Which location: only asked when there is a choice (DEVSIMPLE1); one location is chosen automatically.
    if (locations.length !== 1) {
      const chips = el("div", {
        cls: "tablets-chips",
        attrs: { role: "group", "aria-label": "Which location will it live at?" },
      });
      for (const location of sortByName(locations, (listed) => listed.name)) {
        const chip = el("button", {
          cls: "tablets-chip",
          text: location.name,
          testid: "tablets-location-chip",
          attrs: { type: "button", "aria-pressed": String(form.locationId === location.id) },
        });
        chip.dataset["locationId"] = location.id;
        chip.addEventListener("click", () => {
          form = { ...form, locationId: location.id };
          formError = undefined;
          drawSheet();
        });
        chips.append(chip);
      }
      if (locations.length === 0) {
        chips.append(
          el("p", { cls: "tablets-empty", text: "No locations are listed for this business." }),
        );
      }
      shell.append(
        field(el("p", { cls: "tablets-label", text: "Which location will it live at?" }), chips),
      );
    }

    // Pick a name
    const names = el("div", {
      cls: "tablets-chips",
      attrs: { role: "group", "aria-label": "Device name" },
    });
    for (const name of [...TABLET_NAME_PRESETS, OTHER_NAME]) {
      const chip = el("button", {
        cls: "tablets-chip",
        text: name,
        testid: "tablets-name-chip",
        attrs: { type: "button", "aria-pressed": String(form.namePreset === name) },
      });
      chip.dataset["name"] = name;
      chip.addEventListener("click", () => {
        form = { ...form, namePreset: name };
        formError = undefined;
        drawSheet();
        if (name === OTHER_NAME) {
          shellInput()?.focus();
        }
      });
      names.append(chip);
    }
    const nameField = field(
      el("p", { cls: "tablets-label", text: "Device name (optional)" }),
      names,
    );
    // UX89: nothing chosen means the device asks for its own name when it accepts the code.
    nameField.append(
      el("p", {
        cls: "tablets-help",
        text: optionalNameHelp(),
        testid: "tablets-name-optional-help",
      }),
    );
    if (form.namePreset === OTHER_NAME) {
      nameField.append(
        el("p", {
          cls: "tablets-help",
          text: `Up to ${MAX_NAME_LENGTH} letters.`,
          attrs: { id: "tablets-name-help" },
        }),
      );
      const input = el("input", {
        cls: "field-input",
        testid: "tablets-name-input",
        attrs: {
          type: "text",
          maxlength: String(MAX_NAME_LENGTH),
          autocomplete: "off",
          inputmode: "text",
          enterkeyhint: "done",
          autocapitalize: "words",
          "aria-label": "Device name",
        },
      });
      describeSetting(input, "tablets-name-help");
      input.value = form.customName;
      input.addEventListener("input", () => {
        form = { ...form, customName: input.value };
        formError = undefined;
        updateMakeButton();
      });
      nameField.append(input);
    }
    shell.append(nameField);

    if (formError !== undefined) shell.append(message("danger", undefined, formError));

    const foot = el("div", { cls: "tablets-sheet-foot" });
    foot.append(
      button("Cancel", "tablets-secondary tablets-sheet-button", "tablets-add-cancel", () =>
        closeSheet(),
      ),
    );
    const make = button(
      making ? "Making code" : "Make code",
      "tablets-primary",
      "tablets-make-code",
      () => void makeCode(),
    );
    make.disabled = making || !canMakeCode(form, locations);
    foot.append(make);
    shell.append(foot);
    return shell;
  }

  function shellInput(): HTMLInputElement | null {
    return sheetHost?.querySelector<HTMLInputElement>('[data-testid="tablets-name-input"]') ?? null;
  }

  function optionalNameHelp(): string {
    return leavesNamingToDevice(form)
      ? "No name chosen. The device will ask for its own name when it is set up."
      : "You can leave this and let the device name itself.";
  }

  function updateMakeButton(): void {
    const help = sheetHost?.querySelector<HTMLElement>(
      '[data-testid="tablets-name-optional-help"]',
    );
    if (help !== null && help !== undefined) help.textContent = optionalNameHelp();
    const make = sheetHost?.querySelector<HTMLButtonElement>('[data-testid="tablets-make-code"]');
    if (make !== null && make !== undefined)
      make.disabled = making || !canMakeCode(form, locations);
  }

  async function makeCode(): Promise<void> {
    const locationId = chosenLocationId(form, locations);
    const name = chosenName(form);
    if (making || locationId === undefined || name === undefined) return;
    making = true;
    formError = undefined;
    drawSheet();
    const signature = `${locationId}|${name}`;
    if (issueAttempt === undefined || issueAttempt.signature !== signature) {
      issueAttempt = { signature, key: deps.newKey() };
    }
    const result = await issueTabletSetupCode(deps.apiBaseUrl(), deps.tenantId(), {
      idempotencyKey: issueAttempt.key,
      locationId,
      displayName: name,
    });
    if (!deps.isCurrent() || sheet?.kind !== "add") return;
    making = false;
    if (
      (result.status === 201 || result.status === 200) &&
      typeof result.body.code === "string" &&
      result.body.setupCode !== undefined
    ) {
      issueAttempt = undefined;
      const locationName = locations.find((location) => location.id === locationId)?.name ?? "";
      sheet = {
        kind: "code",
        code: result.body.code,
        setupCode: result.body.setupCode,
        businessName,
        locationName,
        error: undefined,
      };
      drawSheet();
      void refresh();
      return;
    }
    const failure = issueFailureView(
      result.status,
      result.body as Partial<ApiErrorBody>,
      result.networkError,
    );
    if (failure.kind === "limit") {
      issueAttempt = undefined;
      sheet = { kind: "limit" };
      drawSheet();
      void refresh();
      return;
    }
    formError =
      result.status === 200 && result.body.replayed === true
        ? (result.body.message ?? failure.detail)
        : failure.detail;
    drawSheet();
  }

  // --- The code, shown once

  function codeSheet(current: Extract<Sheet, { kind: "code" }>): HTMLElement {
    const { shell } = sheetShell(
      `Setup code for ${current.setupCode.displayName}`,
      "tablets-code-sheet",
    );
    const countdown = codeCountdown(current.setupCode, now());
    const box = el("div", { cls: "tablets-codebox" });
    box.append(
      el("p", { cls: "tablets-bigcode", text: current.code, testid: "tablets-code-value" }),
    );
    const line = el("div", { cls: "tablets-timerline" });
    const count = el("span", {
      cls: `tablets-count${countdown.low ? " is-low" : ""}`,
      text: countdown.text,
      testid: "tablets-code-countdown",
    });
    count.dataset["low"] = String(countdown.low);
    line.append(el("span", { text: "Ends in" }), count);
    const bar = el("div", {
      cls: `tablets-bar${countdown.low ? " is-low" : ""}`,
      testid: "tablets-code-bar",
    });
    const fill = el("i", { testid: "tablets-code-bar-fill" });
    fill.style.width = `${Math.round(countdown.fraction * 100)}%`;
    bar.append(fill);
    box.append(line, bar);
    shell.append(box);

    const facts = el("dl", { cls: "tablets-binds" });
    facts.append(
      el("dt", { text: "Business" }),
      el("dd", { text: current.businessName, testid: "tablets-code-business" }),
      el("dt", { text: "Location" }),
      el("dd", { text: current.locationName, testid: "tablets-code-location-name" }),
      el("dt", { text: "Name" }),
      el("dd", { text: current.setupCode.displayName, testid: "tablets-code-name" }),
    );
    shell.append(facts);
    shell.append(
      message(
        "warn",
        "This code shows once.",
        "Enter it on the new device now. If you close this, cancel it from the list and make a new one.",
      ),
    );
    if (current.error !== undefined) shell.append(message("danger", undefined, current.error));
    const foot = el("div", { cls: "tablets-sheet-foot" });
    foot.append(
      button(
        "Cancel code",
        "tablets-secondary tablets-sheet-button",
        "tablets-code-cancel",
        () => void cancelShownCode(current.setupCode.id),
      ),
      button("Done", "tablets-primary", "tablets-code-done", () => {
        closeSheet();
        render();
      }),
    );
    shell.append(foot);
    return shell;
  }

  async function cancelShownCode(codeId: string): Promise<void> {
    const result = await revokeTabletSetupCode(deps.apiBaseUrl(), deps.tenantId(), codeId);
    if (!deps.isCurrent()) return;
    if (result.status === 200) {
      closeSheet();
      notice = "That code was cancelled.";
      await refresh();
      return;
    }
    if (sheet?.kind === "code") {
      sheet = { ...sheet, error: failureText(result) };
      drawSheet();
    }
  }

  async function cancelWaitingCode(codeId: string): Promise<void> {
    const result = await revokeTabletSetupCode(deps.apiBaseUrl(), deps.tenantId(), codeId);
    if (!deps.isCurrent()) return;
    notice = result.status === 200 ? "That code was cancelled." : failureText(result);
    await refresh();
  }

  function failureText(result: ApiResult<Partial<ApiErrorBody>>): string {
    if (result.networkError)
      return "Could not reach the server. Check your connection and try again.";
    return result.body?.message ?? "Something went wrong. Try again.";
  }

  // --- The sixth code

  function limitSheet(): HTMLElement {
    const failure = issueFailureView(409, { error: "setup_code_limit" }, false);
    const { shell } = sheetShell(failure.title, "tablets-limit-sheet");
    shell.append(message("warn", failure.headline, failure.detail));
    const foot = el("div", { cls: "tablets-sheet-foot" });
    foot.append(
      button("OK", "tablets-primary", "tablets-limit-ok", () => {
        closeSheet();
        render();
      }),
    );
    shell.append(foot);
    return shell;
  }

  // --- Revoke

  function openRevoke(row: TabletRow): void {
    openSheet({ kind: "revoke", row, error: undefined, busy: false });
  }

  function revokeSheet(current: Extract<Sheet, { kind: "revoke" }>): HTMLElement {
    const { shell } = sheetShell(`Revoke ${current.row.name}?`, "tablets-revoke-sheet");
    const facts = el("dl", { cls: "tablets-binds" });
    facts.append(
      el("dt", { text: "Location" }),
      el("dd", { text: current.row.locationName }),
      el("dt", { text: "Last used" }),
      el("dd", { text: capitalize(current.row.lastUsed.replace(/^Last used /, "")) }),
    );
    shell.append(
      facts,
      message(
        "danger",
        "Anyone signed in on it is signed out right away.",
        "It cannot be used again until you make a new code and set it up. Open tabs and sales stay as they are.",
      ),
    );
    if (current.error !== undefined) shell.append(message("danger", undefined, current.error));
    const foot = el("div", { cls: "tablets-sheet-foot" });
    foot.append(
      button("Keep device", "tablets-secondary tablets-sheet-button", "tablets-revoke-keep", () =>
        closeSheet(),
      ),
    );
    const confirm = button(
      current.busy ? "Revoking" : "Revoke device",
      "tablets-danger",
      "tablets-revoke-confirm",
      () => void confirmRevoke(current),
    );
    confirm.disabled = current.busy;
    foot.append(confirm);
    shell.append(foot);
    return shell;
  }

  async function confirmRevoke(current: Extract<Sheet, { kind: "revoke" }>): Promise<void> {
    if (current.busy) return;
    sheet = { ...current, busy: true, error: undefined };
    drawSheet();
    const result = await revokeTablet(deps.apiBaseUrl(), deps.tenantId(), current.row.id);
    if (!deps.isCurrent()) return;
    if (result.status === 200) {
      closeSheet();
      notice = `${current.row.name} was revoked. Anyone signed in on it was signed out.`;
      await refresh();
      return;
    }
    if (sheet?.kind === "revoke") {
      sheet = { ...sheet, busy: false, error: failureText(result) };
      drawSheet();
    }
  }

  // --- Rename (UX89)

  function openRename(row: TabletRow): void {
    openSheet({ kind: "rename", row, typed: row.name, error: undefined, busy: false });
  }

  function renameSheet(current: Extract<Sheet, { kind: "rename" }>): HTMLElement {
    const { shell } = sheetShell(`Rename ${current.row.name}`, "tablets-rename-sheet");
    shell.append(
      el("p", {
        cls: "tablets-help",
        text: `Up to ${MAX_NAME_LENGTH} letters.`,
        attrs: { id: "tablets-rename-help" },
      }),
    );
    const input = el("input", {
      cls: "field-input",
      testid: "tablets-rename-input",
      attrs: {
        type: "text",
        maxlength: String(MAX_NAME_LENGTH),
        autocomplete: "off",
        inputmode: "text",
        enterkeyhint: "done",
        autocapitalize: "words",
        "aria-label": "Device name",
      },
    });
    describeSetting(input, "tablets-rename-help");
    input.value = current.typed;
    input.addEventListener("input", () => {
      if (sheet?.kind === "rename") sheet = { ...sheet, typed: input.value };
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void confirmRename();
      }
    });
    shell.append(input);
    if (current.error !== undefined) shell.append(message("danger", undefined, current.error));
    const foot = el("div", { cls: "tablets-sheet-foot" });
    foot.append(
      button("Cancel", "tablets-secondary tablets-sheet-button", "tablets-rename-cancel", () =>
        closeSheet(),
      ),
    );
    const save = button(
      current.busy ? "Saving" : "Save name",
      "tablets-primary",
      "tablets-rename-save",
      () => void confirmRename(),
    );
    save.disabled = current.busy;
    foot.append(save);
    shell.append(foot);
    return shell;
  }

  async function confirmRename(): Promise<void> {
    const current = sheet;
    if (current?.kind !== "rename" || current.busy) return;
    const check = checkDeviceName(current.typed, current.row.name);
    if (check.kind === "invalid") {
      sheet = { ...current, error: check.message };
      drawSheet();
      return;
    }
    if (check.kind === "unchanged") {
      closeSheet();
      return;
    }
    sheet = { ...current, busy: true, error: undefined };
    drawSheet();
    const result = await renameTablet(
      deps.apiBaseUrl(),
      deps.tenantId(),
      current.row.id,
      check.name,
    );
    if (!deps.isCurrent()) return;
    if (result.status === 200) {
      closeSheet();
      notice = `Renamed to ${check.name}.`;
      await refresh();
      return;
    }
    if (sheet?.kind === "rename") {
      sheet = { ...sheet, busy: false, error: failureText(result) };
      drawSheet();
    }
  }

  return { refresh, reset, dismissSheets };
}
